/**
 * Class Code Display and Sharing Component
 * Display class code with QR code and sharing options
 */

import { useState } from 'react';
import { Copy, Check, Download, Share2, QrCode } from 'lucide-react';

interface ClassCodeDisplayProps {
    classCode: string;
    experimentTitle: string;
    experimentId: string;
}

export default function ClassCodeDisplay({
    classCode,
    experimentTitle,
    experimentId,
}: ClassCodeDisplayProps) {
    const [copied, setCopied] = useState(false);
    const [showQR, setShowQR] = useState(false);
    
    const joinUrl = `${window.location.origin}/student/join/${classCode}`;
    
    const handleCopy = (text: string) => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };
    
    const handleDownloadQR = () => {
        // Generate QR code and download
        // Using qrcode.react library or similar
        console.log('Downloading QR code...');
    };
    
    const handleShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: experimentTitle,
                    text: `Join my experiment: ${experimentTitle}\nClass Code: ${classCode}`,
                    url: joinUrl,
                });
            } catch (err) {
                console.log('Share cancelled');
            }
        } else {
            handleCopy(joinUrl);
        }
    };
    
    return (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                Share with Students
            </h3>
            
            {/* Class Code Display */}
            <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Class Code
                </label>
                <div className="flex items-center gap-3">
                    <div className="flex-1 px-6 py-4 bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 border-2 border-indigo-300 dark:border-indigo-700 rounded-lg">
                        <p className="text-center text-4xl font-bold font-mono tracking-widest text-indigo-600 dark:text-indigo-400">
                            {classCode}
                        </p>
                    </div>
                    <button
                        onClick={() => handleCopy(classCode)}
                        className="p-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                        title="Copy code"
                    >
                        {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                    </button>
                </div>
            </div>
            
            {/* Join URL */}
            <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Join Link
                </label>
                <div className="flex items-center gap-3">
                    <input
                        type="text"
                        value={joinUrl}
                        readOnly
                        className="flex-1 px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-white font-mono"
                    />
                    <button
                        onClick={() => handleCopy(joinUrl)}
                        className="p-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                        title="Copy link"
                    >
                        {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                    </button>
                </div>
            </div>
            
            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-3">
                <button
                    onClick={() => setShowQR(!showQR)}
                    className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
                >
                    <QrCode className="w-5 h-5" />
                    {showQR ? 'Hide' : 'Show'} QR Code
                </button>
                <button
                    onClick={handleShare}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2"
                >
                    <Share2 className="w-5 h-5" />
                    Share
                </button>
            </div>
            
            {/* QR Code Section */}
            {showQR && (
                <div className="mt-6 p-6 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                    <div className="flex flex-col items-center">
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                            Students can scan this QR code to join
                        </p>
                        
                        {/* QR Code Placeholder */}
                        <div className="w-48 h-48 bg-white border-4 border-gray-300 rounded-lg flex items-center justify-center mb-4">
                            <div className="text-center">
                                <QrCode className="w-24 h-24 text-gray-400 mx-auto mb-2" />
                                <p className="text-xs text-gray-500">QR Code will appear here</p>
                                <p className="text-xs text-gray-400 mt-1">
                                    Install: npm install qrcode.react
                                </p>
                            </div>
                        </div>
                        
                        <button
                            onClick={handleDownloadQR}
                            className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors flex items-center gap-2"
                        >
                            <Download className="w-4 h-4" />
                            Download QR Code
                        </button>
                    </div>
                </div>
            )}
            
            {/* Instructions */}
            <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <h4 className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-2">
                    How students join:
                </h4>
                <ol className="text-sm text-blue-800 dark:text-blue-400 space-y-1 list-decimal list-inside">
                    <li>Go to HoloLab student portal</li>
                    <li>Click "Join Experiment"</li>
                    <li>Enter the class code: <span className="font-mono font-bold">{classCode}</span></li>
                    <li>Start learning!</li>
                </ol>
            </div>
        </div>
    );
}

// Compact version for dashboard cards
export function ClassCodeBadge({ classCode }: { classCode: string }) {
    const [copied, setCopied] = useState(false);
    
    const handleCopy = () => {
        navigator.clipboard.writeText(classCode);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };
    
    return (
        <button
            onClick={handleCopy}
            className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-100 dark:bg-indigo-900 text-indigo-600 dark:text-indigo-400 rounded-lg font-mono font-bold text-sm hover:bg-indigo-200 dark:hover:bg-indigo-800 transition-colors"
            title="Click to copy"
        >
            <span>{classCode}</span>
            {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
        </button>
    );
}
